import React from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardMedia,
  Grid, 
  Paper, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Divider, 
  Alert, 
  AlertTitle, 
  Link, 
  Button,
  CardHeader,
  Chip
} from '@mui/material';
import AppsIcon from '@mui/icons-material/Apps';
import BuildIcon from '@mui/icons-material/Build';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SchoolIcon from '@mui/icons-material/School';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SecurityIcon from '@mui/icons-material/Security';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';

interface GettingStartedTabProps {
  setTabValue: (value: number) => void;
}

// Simple feature badge component
const FeatureBadge: React.FC<{ label: string; variant?: 'primary' | 'warning' | 'secondary' }> = ({ 
  label, 
  variant = 'primary' 
}) => (
  <Chip
    label={label}
    size="small"
    color={variant}
    variant="outlined"
    sx={{
      fontSize: '0.65rem',
      height: 18,
      ml: 1,
      '& .MuiChip-label': { px: 0.8 }
    }}
  />
);

const GettingStartedTab: React.FC<GettingStartedTabProps> = ({ setTabValue }) => {
  return (
    <>
      {/* Welcome Section - Minor text updates */}
      <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2, backgroundColor: 'primary.lighter', borderLeft: '5px solid', borderColor: 'primary.main' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AppsIcon sx={{ fontSize: '2.5rem', mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
            Welcome to AppConfig²
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" paragraph>
          Your comprehensive platform for <strong>managing, testing, and configuring</strong> Microsoft Entra ID 
          applications with confidence and ease.
        </Typography>
        <Typography variant="body2" color="text.primary" paragraph>
          Whether you're a developer testing authentication flows, an administrator managing application 
          permissions, or an architect reviewing your tenant's application portfolio, AppConfig² provides 
          the tools and insights you need to work efficiently.
        </Typography>
        
        {/* Key Value Propositions */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
              <ManageSearchIcon sx={{ color: 'primary.main', mr: 1 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.dark' }}>
                  Discover & Explore
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Browse your entire application portfolio with intelligent search and filtering
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
              <VpnKeyIcon sx={{ color: 'primary.main', mr: 1 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.dark' }}>
                  Test & Validate
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Verify authentication flows and analyze tokens in real-time
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', p: 2, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1 }}>
              <BuildIcon sx={{ color: 'primary.main', mr: 1 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.dark' }}>
                  Configure & Manage
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use specialized tools and guided workflows for complex scenarios
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Quick Start Hint */}
        <Alert severity="info" sx={{ mt: 3, borderRadius: 2, bgcolor: 'rgba(13, 60, 97, 0.08)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                👈 Start with the Sidebar
              </Typography>
              <Typography variant="body2">
                Everything begins with the navigation on the left. Click <strong>Applications</strong> to 
                explore your tenant, or browse other sections to discover what AppConfig² can do.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setTabValue(1)}
              sx={{ ml: 2, minWidth: 'auto', whiteSpace: 'nowrap' }}
            >
              View Applications
            </Button>
          </Box>
        </Alert>
      </Paper>

      {/* Interface Overview Section - Add badges to sidebar items */}
      <Card sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Interface Overview</Typography>
          <Typography paragraph variant="body2" color="text.secondary">
            AppConfig² features an intuitive interface designed to help you work efficiently with your Entra ID applications.
            Everything starts with the Sidebar navigation on the left.
          </Typography>
          
          {/* Sidebar Emphasis Section */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>Start Here: Sidebar Navigation</AlertTitle>
            <Typography variant="body2">
              The <strong>Sidebar on the left</strong> is your main navigation hub. Click any section to access its features instantly. 
              The Sidebar remains visible throughout your session, making it easy to switch between different areas of the application.
            </Typography>
          </Alert>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#f5f9ff', border: '2px solid #1976d2', borderRadius: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
                  📋 Sidebar Navigation (Start Here!)
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                  Click any item in the left sidebar to begin:
                </Typography>
                <List dense>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon><AppsIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>Applications</Typography>}
                      secondary="Browse and manage all your Entra ID applications"
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon><BuildIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>Tools</Typography>}
                      secondary="Access specialized tools including Graph Explorer, Manifest Editor, and more"
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon><PlayArrowIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>Actions</Typography>
                          <FeatureBadge label="AppConfig² Only" />
                        </Box>
                      }
                      secondary="Perform common tasks like app registration, backup, and management"
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon><SchoolIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>Documentation</Typography>}
                      secondary="Access guides and reference materials (you're here now!)"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: '#f5f9ff' }}>
                <Typography variant="subtitle1" gutterBottom>🧪 App Tester Panel</Typography>
                <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
                  Available when you select an application:
                </Typography>
                <List dense>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon><VpnKeyIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Authentication Testing" 
                      secondary="Test various authentication flows for your applications"
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon><SecurityIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Permission Validation" 
                      secondary="Verify that permissions are correctly configured"
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon><ContentCopyIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Token Analysis" 
                      secondary="View and analyze tokens issued during authentication"
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5 }}>
                    <ListItemIcon><VerifiedUserIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary="Configuration Verification" 
                      secondary="Ensure your app configurations are valid and secure"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>        
        </CardContent>
      </Card>      
      
      {/* Key Features Section - Add badge to Actions card */}
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Key Features</Typography>
      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardMedia
              component="div"
              sx={{ 
                height: 120, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: '#e3f2fd' 
              }}
            >
              <AppsIcon sx={{ fontSize: 60, color: '#1976d2' }} />
            </CardMedia>
            <CardContent>
              <Typography variant="h6" gutterBottom>Application Management</Typography>
              <Typography variant="body2" color="text.secondary">
                Browse, search, and manage all your Entra ID applications in one place. View detailed
                configurations and quickly access key settings.
              </Typography>
              <Button 
                size="small" 
                color="primary" 
                sx={{ mt: 2 }} 
                onClick={() => setTabValue(1)}
                endIcon={<ArrowRightIcon />}
              >
                Explore Applications
              </Button>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardMedia
              component="div"
              sx={{ 
                height: 120, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: '#e8f5e9' 
              }}
            >
              <BuildIcon sx={{ fontSize: 60, color: '#2e7d32' }} />
            </CardMedia>
            <CardContent>
              <Typography variant="h6" gutterBottom>Testing Tools</Typography>
              <Typography variant="body2" color="text.secondary">
                Comprehensive tools to test authentication flows, decode tokens, validate permissions, 
                and ensure your applications are correctly configured.
              </Typography>
              <Button 
                size="small" 
                color="primary" 
                sx={{ mt: 2 }} 
                onClick={() => setTabValue(2)}
                endIcon={<ArrowRightIcon />}
              >
                Discover Tools
              </Button>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardMedia
              component="div"
              sx={{ 
                height: 120, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: '#fff3e0' 
              }}
            >
              <PlayArrowIcon sx={{ fontSize: 60, color: '#ed6c02' }} />
            </CardMedia>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" gutterBottom sx={{ mb: 0, flex: 1 }}>Quick Actions</Typography>
                <FeatureBadge label="AppConfig² Only" />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Perform common tasks quickly and efficiently, such as registering new apps,
                backing up configurations, and managing permissions.
              </Typography>
              <Button 
                size="small" 
                color="primary" 
                sx={{ mt: 2 }} 
                onClick={() => setTabValue(3)}
                endIcon={<ArrowRightIcon />}
              >
                View Actions
              </Button>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ height: '100%', borderRadius: 2 }}>
            <CardMedia
              component="div"
              sx={{ 
                height: 120, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                backgroundColor: '#f3e5f5' 
              }}
            >
              <VerifiedUserIcon sx={{ fontSize: 60, color: '#9c27b0' }} />
            </CardMedia>
            <CardContent>
              <Typography variant="h6" gutterBottom>App Tester</Typography>
              <Typography variant="body2" color="text.secondary">
                The main feature of AppConfig² - test authentication flows, analyze tokens, 
                validate permissions, and verify your application configurations in real-time.
              </Typography>
              <Button 
                size="small" 
                color="primary" 
                sx={{ mt: 2 }} 
                onClick={() => setTabValue(4)}
                endIcon={<ArrowRightIcon />}
              >
                Learn App Tester
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Getting Started Steps - Update step 4 to mention tool variations */}
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Quick Start Guide</Typography>
      
      <Card sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent>
          <List>
            <ListItem alignItems="flex-start">
              <ListItemIcon>
                <Box 
                  sx={{ 
                    width: 30, 
                    height: 30, 
                    borderRadius: '50%', 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontWeight: 'bold'
                  }}
                >
                  1
                </Box>
              </ListItemIcon>
              <ListItemText 
                primary="Select an Application to Work With" 
                secondary={
                  <>
                    <Typography component="span" variant="body2" color="text.primary">
                      Navigate to the Applications section and browse or search for the application you want to manage.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      You can filter applications by type, name, or other properties to quickly find what you need.
                    </Typography>
                  </>
                }
              />
            </ListItem>
            
            <Divider variant="inset" component="li" />
            
            <ListItem alignItems="flex-start">
              <ListItemIcon>
                <Box 
                  sx={{ 
                    width: 30, 
                    height: 30, 
                    borderRadius: '50%', 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontWeight: 'bold' 
                  }}
                >
                  2
                </Box>
              </ListItemIcon>
              <ListItemText 
                primary="Explore Application Details" 
                secondary={
                  <>
                    <Typography component="span" variant="body2" color="text.primary">
                      Review the application's configuration details, authentication settings, and permissions.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      This gives you a comprehensive view of how the application is configured in your Entra ID tenant.
                    </Typography>
                  </>
                }
              />
            </ListItem>
            
            <Divider variant="inset" component="li" />
            
            <ListItem alignItems="flex-start">
              <ListItemIcon>
                <Box 
                  sx={{ 
                    width: 30, 
                    height: 30, 
                    borderRadius: '50%', 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontWeight: 'bold' 
                  }}
                >
                  3
                </Box>
              </ListItemIcon>
              <ListItemText 
                primary="Test Authentication Flows" 
                secondary={
                  <>
                    <Typography component="span" variant="body2" color="text.primary">
                      Use the App Tester panel to test various authentication scenarios for your application.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      You can test authentication flows, examine tokens, and validate that permissions are working as expected.
                    </Typography>
                  </>
                }
              />
            </ListItem>
            
            <Divider variant="inset" component="li" />
            
            <ListItem alignItems="flex-start">
              <ListItemIcon>
                <Box 
                  sx={{ 
                    width: 30, 
                    height: 30, 
                    borderRadius: '50%', 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontWeight: 'bold' 
                  }}
                >
                  4
                </Box>
              </ListItemIcon>
              <ListItemText 
                primary="Use Specialized Tools" 
                secondary={
                  <>
                    <Typography component="span" variant="body2" color="text.primary">
                      Explore the Tools section to access specialized functionality like the JWT Decoder, Graph Explorer 
                      <FeatureBadge label="Read-only in AppTesting" variant="warning" />, 
                      Manifest Editor <FeatureBadge label="AppConfig² Only" />, and more.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      These tools help you perform detailed configuration and troubleshooting tasks.
                    </Typography>
                  </>
                }
              />
            </ListItem>
            
            <Divider variant="inset" component="li" />
            
            <ListItem alignItems="flex-start">
              <ListItemIcon>
                <Box 
                  sx={{ 
                    width: 30, 
                    height: 30, 
                    borderRadius: '50%', 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontWeight: 'bold' 
                  }}
                >
                  5
                </Box>
              </ListItemIcon>
              <ListItemText 
                primary="Follow Guided Workflows" 
                secondary={
                  <>
                    <Typography component="span" variant="body2" color="text.primary">
                      For common scenarios like configuring custom claims or exposing APIs, use the step-by-step workflows.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      These guided workflows ensure you complete all necessary steps for complex configuration tasks.
                    </Typography>
                  </>
                }
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>
      
      {/* Tips and Best Practices - Update management section */}
      <Card sx={{ borderRadius: 2 }}>
        <CardHeader title="Tips and Best Practices" />
        <Divider />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, bgcolor: '#f5f9ff', borderRadius: 2, height: '100%' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ color: 'primary.main' }}>
                  Security First
                </Typography>
                <List dense disablePadding>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Use the principle of least privilege when configuring permissions" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Regularly rotate client secrets and certificates" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Verify redirect URIs are specific and controlled" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Use application roles and conditional access where appropriate" />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, bgcolor: '#f5f9ff', borderRadius: 2, height: '100%' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ color: 'primary.main' }}>
                  Efficient Management
                </Typography>
                <List dense disablePadding>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Document your application configurations" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText 
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          Use the backup functionality to preserve configurations
                          <FeatureBadge label="AppConfig² Only" />
                        </Box>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Establish consistent naming conventions for applications" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><CheckCircleOutlineIcon color="primary" fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Test changes in non-production environments first" />
                  </ListItem>
                </List>
              </Box>
            </Grid>
          </Grid>
          
          {/* <Alert severity="info" sx={{ mt: 3 }}>
            <AlertTitle>Need More Help?</AlertTitle>
            <Typography variant="body2">
              For detailed information about specific features, explore the other tabs in this documentation.
              If you encounter issues, check the <Link href="#" onClick={() => setTabValue(7)} color="primary">Troubleshooting</Link> section
              or refer to the <Link href="#" onClick={() => setTabValue(8)} color="primary">Best Practices</Link> for guidance.
            </Typography>
          </Alert> */}
        </CardContent>
      </Card>
    </>
  );
};

export default GettingStartedTab;